import moment from "moment";

const lower = "(?=.*[a-z])";
const upper = "(?=.*[A-Z])";
const number = "(?=.*[0-9])";
// Double escape \s since this will be used as a substring and re-escaped in the regex;
const special = "(?=.*[-!@#$%^&*()_+|~=`{}\\[\\]:\";'<>?,.\\\\/])";
const length = "(?=.{12,})";

// const set1 = `(${lower}${upper}${number})`;
// const set2 = `(${lower}${upper}${special})`;
// const set3 = `(${lower}${number}${special})`;
// const set4 = `(${upper}${number}${special})`;

export const passwordRegex = new RegExp(
  `^$|(${lower}${upper}${number}${special})${length}`
);

export default class ValidationUtils {
  static validate(model, validationRules, roleId) {
    let errors = {};
    for (let [fieldName, fieldRules] of this.entries(validationRules)) {
      let fieldValue = eval(`model.${fieldName}`);
      const regex = /-\d+/;
      if (regex.test(fieldName)) {
        // Check if `fieldname` contains a numeric value.
        // The `eval` function interprets strings with number as arithmetic expressions
        // when numeric values are present, which can lead to unintended
        // behavior or errors.
        fieldValue = model[fieldName];
      }
      if (!fieldValue && fieldValue !== 0 && fieldValue !== false) {
        fieldValue = "";
      }
      for (let [ruleName, ruleValue] of this.entries(fieldRules)) {
        const validationFunction = this[ruleName];
        const ruleCustomMessageKey = ruleValue.customMessageKey;
        const ruleCustomErrorLevel = ruleValue.customErrorLevel || null;
        const rulePushErrorToField = ruleValue.pushErrorToField || null;
        ruleValue = ruleName === "array" ? ruleValue : ruleValue.value;
        const validationResult = validationFunction(
          fieldValue,
          ruleValue,
          model,
          roleId
        );
        let validationPassed = !validationResult;
        if (Array.isArray(validationResult)) {
          validationPassed = validationResult.every((result) => {
            return (
              Object.keys(result).length === 0 && result.constructor === Object
            );
          });
        }
        if (!!validationFunction && !validationPassed) {
          let error = {};
          if (ruleName === "array") {
            error = validationResult;
          } else {
            error = {
              level:
                ruleCustomErrorLevel ||
                VALIDATION_RULE_SETTINGS[ruleName].level ||
                null,
              state: VALIDATION_RULE_SETTINGS[ruleName].state,
              messageKey: VALIDATION_RULE_SETTINGS[ruleName].messageKey(
                ruleCustomMessageKey || ruleValue.customMessageKey
              ),
              values: {
                ...(!!VALIDATION_RULE_SETTINGS[ruleName].values &&
                  VALIDATION_RULE_SETTINGS[ruleName].values(
                    ruleValue.value || ruleValue,
                    model
                  )),
              },
            };
          }

          errors = ValidationUtils.setError(
            errors,
            error,
            rulePushErrorToField || fieldName
          );
          break;
        }
      }
    }
    return errors;
  }

  static setError(errorsObj, error, fieldName) {
    if (error.level === "form") {
      return {
        ...errorsObj,
        _error: { ...errorsObj._error, [fieldName]: error },
      };
    }

    let path = fieldName
      .replace(/(']\['|\['|']\.)/g, ".")
      .replace("']", "")
      .split(".");
    if (error.level === "root") {
      path.push("_error");
    }
    let prev = "";
    let last = path[path.length - 1];

    path.map((child) => {
      if (child == last) {
        return eval(`errorsObj${prev}['${child}'] = error;`);
      }

      eval(
        `errorsObj${prev}['${child}'] = errorsObj${prev}['${child}'] || {};`
      );
      prev += `['${child}']`;
    });

    return errorsObj;
  }

  static array(valueArray = [], ruleValue) {
    if (valueArray) {
      return valueArray.map((value) =>
        ValidationUtils.validate(value, ruleValue)
      );
    }
  }

  static atLeastOne(value, ruleValue) {
    const hasAtLeastOne = Array.isArray(value)
      ? value.findIndex((fields) => {
        return fields[ruleValue.field] == ruleValue.expectedValue;
      }) >= 0
      : false;

    return !hasAtLeastOne;
  }

  static fieldMustHaveValueIfOtherValueSelected(value, ruleValue, model) {
    const otherValue = model[ruleValue.otherValueKey];
    if (otherValue !== ruleValue.otherExpectedValue) return false;

    if (value && Number.isInteger(value)) {
      value = value.toString();
    }
    if (ruleValue.expectedFieldValue && Number.isInteger(ruleValue.expectedFieldValue)) {
      ruleValue.expectedFieldValue = ruleValue.expectedFieldValue.toString();
    }
    const isExpectedValueSet = value === ruleValue.expectedFieldValue;

    return !isExpectedValueSet;
  }

  static atLeastOneAcrossMultipleFields(value, ruleValue, model) {
    const { field, expectedValue, otherFields } = ruleValue;

    //Start with main field
    let hasAtLeastOne = Array.isArray(value)
      ? value.findIndex((fields) => {
        return fields[field] == expectedValue;
      }) >= 0
      : false;

    if (!hasAtLeastOne) {
      //Look through the others
      hasAtLeastOne = otherFields
        .map((otherField) => {
          const F = model[otherField];
          return Array.isArray(F)
            ? F.findIndex((fields) => {
              return fields[field] == expectedValue;
            }) >= 0
            : false;
        })
        .includes(true);
    }

    return !hasAtLeastOne;
  }

  static fileNameContainsSubString(value, ruleValue) {
    if (value.length) {
      for (let index in value) {
        if (value.hasOwnProperty(index)) {
          const file = value[index];
          const validationError =
            ruleValue && file.name.indexOf(ruleValue) === -1;
          if (validationError) {
            return validationError;
          }
        }
      }
    }
  }

  static fileOfExtension(value, ruleValue) {
    if (value.length) {
      for (let index in value) {
        if (value.hasOwnProperty(index)) {
          const file = value[index];
          const validationError =
            ruleValue &&
            !file.name.match(new RegExp(`(${ruleValue.join("|")})$`, "i"));
          if (validationError) {
            return validationError;
          }
        }
      }
    }
  }

  static float(value, ruleValue) {
    const decimals = ruleValue.decimals || 2;
    const pattern = new RegExp(`^$|^\\d+((\\.|,)\\d{1,${decimals}})?$`, "i");

    return ruleValue && !pattern.exec(value);
  }

  static higherThan(value, ruleValue, model) {
    const vsValue = model[ruleValue.field];

    if (!value || !vsValue) {
      return false;
    }

    return !(value > vsValue);
  }

  static higherThanValue(value, ruleValue) {
    const vsValue = ruleValue.value;

    if (!value || !vsValue) {
      return false;
    }

    return !(value > vsValue);
  }

  static isRequiredFile(value, ruleValue) {
    return ruleValue && !value;
  }

  static isRequired(value, ruleValue) {
    return ruleValue && value != null && value.toString() == "";
  }

  static isRequiredBoolean(value, ruleValue) {
    return (
      ruleValue &&
      (value == null || value.toString().trim() == "") &&
      value.toString().trim() != "true" &&
      value.toString().trim() != "false"
    );
  }

  static isRequiredTrim(value, ruleValue) {
    return ruleValue && value != null && value.toString().trim() == "";
  }

  static isRequiredTrimIfFieldsValueDoesNotEqual(value, ruleValue) {
    const isEmpty = value != null && value.toString().trim() == "";

    if (ruleValue && ruleValue.expectedValues && ruleValue.expectedValues.includes(value)) {
      return false;
    }
    return isEmpty;
  }

  static isRequiredTrimIfField(value, ruleValue, model) {
    if (model[ruleValue.field]) {
      return value != null && value.toString().trim() == "";
    }
    return false;
  }

  static isRequiredTrimIfFieldHasValue(value, ruleValue, model) {
    if (ruleValue.expectedValues.includes(model[ruleValue.field])) {
      return value != null && value.toString().trim() == "";
    }
    return false;
  }

  static isRequiredTrimIfOtherFieldIsSet(value, ruleValue, model) {
    const otherField = ruleValue.otherField;
    if (! !!otherField) return false;

    const otherFieldValue = model[otherField];
    const excludedValues = ruleValue.excludeOtherFieldValues || [];

    if (otherFieldValue != null && otherFieldValue.toString().trim() != "" && !excludedValues.includes(otherFieldValue)) {
      return value != null && value.toString().trim() == "";
    }
    return false;
  }

  static lowerEqualThanIfFieldHasValue(
    value,
    { otherField, otherFieldValue, limitToApply },
    model
  ) {
    const needValidation = model[otherField] === otherFieldValue;
    return needValidation && value > limitToApply;
  }

  static isRequiredTrimIfUserHasRoleId(value, ruleValue, model, roleId) {
    if (ruleValue.expectedValues.includes(roleId)) {
      return value.toString().trim() == "";
    }
    return false;
  }

  static lowerThan(value, ruleValue, model) {
    const vsValue = model[ruleValue.field];

    if (!value || !vsValue) {
      return false;
    }

    return !(value < vsValue);
  }

 static afterOrEqualOtherDateField(value, ruleValue, model) {
  const thisDate = moment.isMoment(value) ? value : moment(value);
  const otherDate = moment.isMoment(model[ruleValue.field]) ? model[ruleValue.field] : moment(model[ruleValue.field]);

  if (!value || !model[ruleValue.field]) {
    // Returns false if either value is empty
    return false;
  }

  return !thisDate.isSameOrAfter(otherDate);
}

  static lowerThanValue(value, ruleValue) {
    const vsValue = ruleValue.value;

    if (!value || !vsValue) {
      return false;
    }

    return !(value < vsValue);
  }

  static maxLength(value, ruleValue) {
    return value != "" && value != null && value.length > ruleValue;
  }

  static minLength(value, ruleValue) {
    return value != "" && value != null && value.length < ruleValue;
  }

  static minValue(value, ruleValue) {
    return value != null && Number(value) < ruleValue;
  }

  static minValueOrEmpty(value, ruleValue) {
    return value ? Number(value) < ruleValue : false;
  }

  static mustMatch(value, ruleValue, model) {
    return value != null && value != model[ruleValue.field];
  }

  static noSpaces(value, ruleValue) {
    return ruleValue && value.indexOf(" ") >= 0;
  }

  static regex(value, ruleValue) {
    return !ruleValue.pattern.exec(value);
  }

  static valueBetween(value, ruleValue) {
    return !(value >= ruleValue.minimum && value <= ruleValue.maximum);
  }

  static noSpacesBeginning(value, ruleValue) {
    return ruleValue && value.startsWith(" ");
  }

  static noSpacesEnd(value, ruleValue) {
    return ruleValue && value.endsWith(" ");
  }

  static stringSameCharacters(value, ruleValue) {
    if (value[0].repeat(value.length) === value) {
      return ruleValue && value;
    }
    return !ruleValue;
  }
  static mutuallyExclusiveWith(value, ruleValue, model) {
    const checked = !!value;
    const otherChecked = !!model[ruleValue.other];
    return checked && otherChecked;
  }

  static isRequiredIn(value, ruleValue) {
    return Array.isArray(ruleValue) && !ruleValue.includes(value);
  }

  static *entries(obj) {
    if (obj) {
      for (let key of Object.keys(obj)) {
        yield [key, obj[key]];
      }
    }
  }
}

const VALIDATION_RULE_SETTINGS = {
  array: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.array",
  },
  atLeastOne: {
    level: "root",
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.atLeastOne",
  },
  atLeastOneAcrossMultipleFields: {
    level: "form",
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.atLeastOne",
  },
  fileNameContainsSubString: {
    state: "error",
    messageKey: (messageKey) =>
      messageKey || "validation.fileNameContainsSubString",
    values: (value) => ({ ruleValue: value }),
  },
  fieldMustHaveValueIfOtherValueSelected: {
    state: "error",
    messageKey: (messageKey) => messageKey,
    values: (value) => ({ ruleValue: value }),
  },
  fileOfExtension: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.fileOfExtension",
    values: (types) => ({ ruleValue: types.join("/") }),
  },
  fileOfSignature: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.fileOfSignature",
  },
  float: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.float",
  },
  higherThan: {
    messageKey: (messageKey) => messageKey || "validation.higherThan",
    values: (ruleValue, model) => ({
      ruleValue: ruleValue.value || model[ruleValue.field],
    }),
  },
  higherThanValue: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.higherThan",
    values: (value) => {
      if (moment.isMoment(value)) {
        return moment(value).format("DD/MM/YYYY");
      }

      return { ruleValue: value };
    },
  },
  isRequired: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequired",
  },
  isRequiredBoolean: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequired",
  },
  isRequiredFile: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredFile",
  },
  isRequiredTrim: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  isRequiredTrimIfField: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  isRequiredTrimIfFieldHasValue: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  isRequiredTrimIfOtherFieldIsSet: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  lowerEqualThanIfFieldHasValue: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.lowerThan",
  },
  isRequiredTrimIfUserHasRoleId: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  isRequiredTrimIfFieldsValueDoesNotEqual: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
  lowerThan: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.lowerThan",
  },
  afterOrEqualOtherDateField: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.EqualOrGreaterThanStartDate",
  },
  lowerThanValue: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.lowerThan",
    values: (value) => {
      if (moment.isMoment(value)) {
        return moment(value).format("DD/MM/YYYY");
      }

      return { ruleValue: value };
    },
  },
  maxLength: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.maxLength",
    values: (length) => ({ ruleValue: length }),
  },
  minLength: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.minLength",
    values: (length) => ({ ruleValue: length }),
  },
  minValue: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.minValue",
    values: (value) => ({ ruleValue: value.toString() }),
  },
  minValueOrEmpty: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.minValue",
    values: (value) => ({ ruleValue: value.toString() }),
  },
  mustMatch: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.mustMatch",
    values: (value) => ({ ruleValue: value }),
  },
  noSpaces: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.noSpaces",
  },
  regex: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.regex",
  },
  noSpacesBeginning: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.noSpacesBeginning",
  },
  noSpacesEnd: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.noSpacesEnd",
  },
  stringSameCharacters: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.stringSameCharacters",
  },
  valueBetween: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.valueBetween",
    values: (value) => ({
      minimum: value.minimum.toString(),
      maximum: value.maximum.toString(),
    }),
  },
  mutuallyExclusiveWith: {
    state: "error",
    messageKey: (messageKey) =>
      messageKey || "validation.mutuallyExclusiveWith",
    values: (value) => ({ other: value.other }),
  },
  isRequiredIn: {
    state: "error",
    messageKey: (messageKey) => messageKey || "validation.isRequiredTrim",
  },
};



// WEBPACK FOOTER //
// ./src/utils/ValidationUtils.js